import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Clock,
  Download,
  Eye,
  FileText,
  MessageSquare,
  PenLine,
  RefreshCw,
  Send,
  Trash2,
  Upload,
} from "lucide-react";

import { ApiError, api } from "../api/client";
import BottomNav from "../components/BottomNav";
import SignatureModal from "../components/SignatureModal";
import JobDescriptionEditor from "../components/jd/JobDescriptionEditor";
import JobDescriptionView from "../components/jd/JobDescriptionView";
import {
  SIGN_OFF_TEXT,
  issueProblems,
  jdDate,
  type JDContent,
  type JDPage,
  type JDVersion,
} from "../utils/jobDescription";

const pdfUrl = (id: number, download = false) => `/api/job-descriptions/${id}/pdf${download ? "?download=1" : ""}`;

function errText(e: unknown): string {
  return e instanceof ApiError || e instanceof Error ? e.message : "Something went wrong. Please try again.";
}

export default function JobDescriptionPage() {
  const { t } = useTranslation();
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState<JDPage | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setPage(await api.get<JDPage>(employeeId ? `/api/job-descriptions/employee/${employeeId}` : "/api/job-descriptions/me"));
      setError(null);
    } catch (e) {
      setError(errText(e));
    }
  }, [employeeId]);

  useEffect(() => { void load(); }, [load]);

  const managing = !!page && page.can_edit && !page.is_self;

  return (
    <div className="flex min-h-dvh flex-col bg-white safe-x">
      <header className="safe-top mx-auto w-full max-w-4xl px-5 pb-3 pt-4 pr-28">
        <Link
          to={managing ? "/team" : "/home"}
          onClick={(e) => {
            // Came from somewhere in the app (My team, the employee record, Admin → Performance): go back there.
            if (((window.history.state as { idx?: number } | null)?.idx ?? 0) > 0) {
              e.preventDefault();
              navigate(-1);
            }
          }}
          className="mb-1 inline-flex min-h-[36px] items-center gap-1 text-sm font-semibold text-pme-blue"
        >
          <ArrowLeft size={16} aria-hidden /> {t("jd.page.back", "Back")}
        </Link>
        <h1 className="font-display text-3xl tracking-wide text-pme-navy">{t("jd.page.title", "Job description")}</h1>
        {page && (
          <p className="text-sm text-gray-600">
            {page.is_self ? t("jd.page.yours", "Your role at PME Cake") : page.employee.name}
            {page.employee.job_title ? ` · ${page.employee.job_title}` : ""}
          </p>
        )}
      </header>
      <main className="mx-auto w-full max-w-4xl flex-1 px-5 pb-28">
        {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
        {!page && !error && <p className="text-sm text-gray-400">{t("common.loading", "Loading...")}</p>}
        {page && (managing ? <ManagerView page={page} reload={load} /> : <OwnView page={page} reload={load} />)}
      </main>
      <BottomNav />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The employee                                                        */
/* ------------------------------------------------------------------ */

function OwnView({ page, reload }: { page: JDPage; reload: () => Promise<void> }) {
  const { t } = useTranslation();
  const jd = page.current;
  const [agree, setAgree] = useState(false);
  const [signing, setSigning] = useState(false);
  const [asking, setAsking] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  if (!jd || !jd.content) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
        <FileText className="text-gray-400" size={24} aria-hidden />
        <p className="mt-2 font-semibold text-gray-900">{t("jd.own.none", "Your job description isn't in PME People yet")}</p>
        <p className="mt-1 text-sm text-gray-600">
          {t("jd.own.noneHint", "Your manager will write it here and send it to you to read and sign. If you were given one as a document, it is under Documents.")}
        </p>
        <Link to="/documents" className="mt-3 inline-flex min-h-[44px] items-center rounded-xl border border-gray-300 px-4 text-sm font-semibold text-gray-700">
          {t("jd.own.openDocuments", "Open my documents")}
        </Link>
      </div>
    );
  }

  const waiting = jd.status === "issued" || jd.status === "changes_requested";

  return (
    <div className="space-y-5">
      {done && (
        <p className="flex items-start gap-2 rounded-xl bg-green-50 px-4 py-3 text-sm text-green-800" role="status">
          <CheckCircle2 size={18} className="mt-0.5 shrink-0" aria-hidden /> {done}
        </p>
      )}
      {jd.status === "issued" && (
        <Banner tone="blue" icon={<PenLine size={18} aria-hidden />} title={t("jd.own.toSign", "Please read and sign your job description")}>
          {t("jd.own.toSignHint", "{{manager}} sent this on {{date}}. If it describes your role, tick the box at the bottom and sign. If something is wrong, ask for a change.", {
            manager: jd.issued_by_name || t("jd.own.yourManager", "Your manager"),
            date: jdDate(jd.issued_at),
          })}
        </Banner>
      )}
      {jd.status === "changes_requested" && (
        <Banner tone="amber" icon={<MessageSquare size={18} aria-hidden />} title={t("jd.own.asked", "You asked for a change")}>
          <span className="block italic">“{jd.change_comment}”</span>
          <span className="mt-1 block">
            {t("jd.own.askedHint", "Your manager has been told and will send an updated version. If you've talked it through and you're happy with it as it is, you can still sign below.")}
          </span>
        </Banner>
      )}
      {jd.status === "signed" && (
        <Banner tone="green" icon={<CheckCircle2 size={18} aria-hidden />} title={t("jd.own.signed", "You signed this on {{date}}", { date: jdDate(jd.signed_at) })}>
          {t("jd.own.signedHint", "This is your job description in force. A signed copy is in your documents.")}
        </Banner>
      )}

      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-gray-500">
            {t("jd.version", "Version {{n}}", { n: jd.version })}
            {jd.approved_by_name ? ` · ${t("jd.approvedBy", "approved by {{name}}", { name: jd.approved_by_name })}` : ""}
          </p>
          <a href={pdfUrl(jd.id, true)} className="inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border border-gray-200 px-3 text-xs font-semibold text-gray-700 hover:bg-gray-50">
            <Download size={14} aria-hidden /> {t("jd.downloadPdf", "Download PDF")}
          </a>
        </div>
        <JobDescriptionView content={jd.content} />
      </div>

      {waiting && (
        <div className="rounded-2xl border-2 border-pme-blue/30 bg-pme-sky/30 p-4 sm:p-5">
          <h2 className="text-base font-bold text-pme-navy">{t("jd.own.signOff", "Sign-off")}</h2>
          <p className="mt-1 text-sm text-gray-700">{t("jd.own.signOffText", SIGN_OFF_TEXT)}</p>
          <label className="mt-3 flex min-h-[44px] items-start gap-3 rounded-xl bg-white px-3 py-2.5">
            <input type="checkbox" className="mt-0.5 h-5 w-5 accent-pme-blue" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
            <span className="text-sm font-medium text-gray-900">
              {t("jd.own.agree", "I have read this job description and it describes my role")}
            </span>
          </label>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={!agree}
              onClick={() => setSigning(true)}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-pme-blue px-5 text-sm font-semibold text-white disabled:opacity-40"
            >
              <PenLine size={16} aria-hidden /> {t("jd.own.sign", "Sign")}
            </button>
            {jd.status === "issued" && (
              <button
                type="button"
                onClick={() => setAsking(true)}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-700"
              >
                <MessageSquare size={16} aria-hidden /> {t("jd.own.ask", "Ask for a change")}
              </button>
            )}
          </div>
        </div>
      )}

      <History page={page} />

      {signing && (
        <SignatureModal
          doc={{ id: jd.id, title: t("jd.page.title", "Job description") }}
          onSubmit={async (signatureData, signatureType) => {
            await api.post(`/api/job-descriptions/${jd.id}/sign`, { signature_data: signatureData, signature_type: signatureType, agree: true });
          }}
          onClose={(signed) => {
            setSigning(false);
            if (signed) {
              setDone(t("jd.own.thanks", "Thank you. You've signed your job description and a copy is in your documents."));
              void reload();
            }
          }}
        />
      )}
      {asking && (
        <AskChangeDialog
          jdId={jd.id}
          onClose={(sent) => {
            setAsking(false);
            if (sent) {
              setDone(t("jd.own.askSent", "Sent. Your manager has been told what you'd like changed."));
              void reload();
            }
          }}
        />
      )}
    </div>
  );
}

function AskChangeDialog({ jdId, onClose }: { jdId: number; onClose: (sent: boolean) => void }) {
  const { t } = useTranslation();
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const send = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.post(`/api/job-descriptions/${jdId}/request-change`, { comment: comment.trim() });
      onClose(true);
    } catch (e) {
      setErr(errText(e));
      setBusy(false);
    }
  };
  return (
    <Dialog title={t("jd.own.askTitle", "Ask for a change")} onClose={() => onClose(false)}>
      <p className="text-sm text-gray-600">
        {t("jd.own.askHint", "Say what's wrong or missing. Your manager gets an email and can send you an updated version.")}
      </p>
      <textarea
        className="mt-3 min-h-[120px] w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:border-pme-blue focus:outline-none"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder={t("jd.own.askPlaceholder", "e.g. I don't do the month-end reconciliations any more, Thandi took those over in June.")}
        aria-label={t("jd.own.askTitle", "Ask for a change")}
        autoFocus
      />
      {err && <p className="mt-2 text-sm text-red-700">{err}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" onClick={() => onClose(false)} className="min-h-[44px] rounded-xl px-4 text-sm font-semibold text-gray-600">
          {t("common.cancel", "Cancel")}
        </button>
        <button
          type="button"
          disabled={busy || comment.trim().length < 3}
          onClick={() => void send()}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-pme-blue px-4 text-sm font-semibold text-white disabled:opacity-40"
        >
          <Send size={15} aria-hidden /> {t("jd.own.askSend", "Send to my manager")}
        </button>
      </div>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* The line manager / HR                                               */
/* ------------------------------------------------------------------ */

type SaveState = "saved" | "unsaved" | "saving" | "error";

function ManagerView({ page, reload }: { page: JDPage; reload: () => Promise<void> }) {
  const { t } = useTranslation();
  const { current, draft } = page;
  const [content, setContent] = useState<JDContent | null>(draft?.content ?? null);
  const [saveState, setSaveState] = useState<SaveState>("saved");
  const [preview, setPreview] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [notice, setNotice] = useState<{ tone: "ok" | "warn" | "err"; text: string } | null>(null);
  const [starting, setStarting] = useState(false);
  const [busy, setBusy] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const pending = useRef<JDContent | null>(null);
  const draftId = draft?.id;

  useEffect(() => {
    setContent(draft?.content ?? null);
    setSaveState("saved");
  }, [draftId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useCallback(async () => {
    if (!draftId || !pending.current) return true;
    const body = pending.current;
    pending.current = null;
    setSaveState("saving");
    try {
      await api.put(`/api/job-descriptions/${draftId}`, { content: body });
      setSaveState(pending.current ? "unsaved" : "saved");
      return true;
    } catch (e) {
      pending.current = pending.current ?? body;
      setSaveState("error");
      setNotice({ tone: "err", text: errText(e) });
      return false;
    }
  }, [draftId]);

  useEffect(() => () => window.clearTimeout(timer.current), []);
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (pending.current) e.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, []);

  const change = (next: JDContent) => {
    setContent(next);
    pending.current = next;
    setSaveState("unsaved");
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void save(), 900);
  };

  const start = async (startFrom: "blank" | "current" | "document", documentId?: number) => {
    setStarting(true);
    setNotice(null);
    try {
      await api.post(`/api/job-descriptions/employee/${page.employee.id}/draft`, { start: startFrom, document_id: documentId ?? null });
      await reload();
    } catch (e) {
      setNotice({ tone: "err", text: errText(e) });
    } finally {
      setStarting(false);
    }
  };

  const discard = async () => {
    if (!draftId || !window.confirm(t("jd.mgr.discardConfirm", "Throw away this draft? The version in force (if any) stays as it is."))) return;
    window.clearTimeout(timer.current);
    pending.current = null;
    try {
      await api.post(`/api/job-descriptions/${draftId}/discard`);
      setNotice(null);
      await reload();
    } catch (e) {
      setNotice({ tone: "err", text: errText(e) });
    }
  };

  const reread = async () => {
    if (!draftId) return;
    if (!window.confirm(t("jd.mgr.rereadConfirm", "Read the file again? The text in this draft is replaced by what we read from the file."))) return;
    window.clearTimeout(timer.current);
    pending.current = null;
    setBusy(true);
    try {
      const fresh = await api.post<JDVersion>(`/api/job-descriptions/${draftId}/reread`);
      if (fresh.content) setContent(fresh.content);
      setSaveState("saved");
      setNotice({ tone: "ok", text: t("jd.mgr.rereadDone", "Read again from the file. Check it, then carry on.") });
      await reload();
    } catch (e) {
      setNotice({ tone: "err", text: errText(e) });
    } finally {
      setBusy(false);
    }
  };

  const adoptSigned = async () => {
    if (!draftId) return;
    window.clearTimeout(timer.current);
    setBusy(true);
    try {
      if (!(await save())) return;
      await api.post(`/api/job-descriptions/${draftId}/adopt-signed`);
      setNotice({ tone: "ok", text: t("jd.mgr.adoptDone", "Recorded as signed. This is now the job description in force.") });
      await reload();
    } catch (e) {
      setNotice({ tone: "err", text: errText(e) });
    } finally {
      setBusy(false);
    }
  };

  const resend = async (id: number) => {
    try {
      const r = await api.post<{ sent: boolean; message: string }>(`/api/job-descriptions/${id}/resend`);
      setNotice({ tone: r.sent ? "ok" : "warn", text: r.message });
    } catch (e) {
      setNotice({ tone: "err", text: errText(e) });
    }
  };

  const problems = content ? issueProblems(content) : [];
  const first = page.employee.name.split(" ")[0];

  return (
    <div className="space-y-5">
      {notice && (
        <p
          role="status"
          className={`rounded-xl px-4 py-3 text-sm ${notice.tone === "ok" ? "bg-green-50 text-green-800" : notice.tone === "warn" ? "bg-amber-50 text-amber-900" : "bg-red-50 text-red-700"}`}
        >
          {notice.text}
        </p>
      )}

      <StatusCard current={current} hasDraft={!!draft} first={first} onResend={resend} />

      {!draft && (
        <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
          <h2 className="text-base font-bold text-pme-navy">
            {current ? t("jd.mgr.changeTitle", "Change {{name}}'s job description", { name: first }) : t("jd.mgr.writeTitle", "Write {{name}}'s job description", { name: first })}
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            {t("jd.mgr.startHint", "Edit it here instead of uploading a new file. When you're happy, issue it and {{name}} gets an email to read and sign it. Nothing changes for them until you issue it.", { name: first })}
          </p>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {current && (
              <StartOption
                icon={<PenLine size={18} aria-hidden />}
                title={t("jd.mgr.fromCurrent", "Edit the current version")}
                hint={t("jd.mgr.fromCurrentHint", "Start from version {{n}} and change what's needed.", { n: current.version })}
                disabled={starting}
                primary
                onClick={() => void start("current")}
              />
            )}
            {page.importable.map((d) => (
              <StartOption
                key={d.id}
                icon={<Upload size={18} aria-hidden />}
                title={t("jd.mgr.fromDoc", "Read from “{{title}}”", { title: d.title })}
                hint={t("jd.mgr.fromDocHint", "We copy the text from the {{type}} into the form, so you only check and tidy it.", { type: d.file_type.toUpperCase() })}
                disabled={starting}
                primary={!current && d.looks_like_jd}
                onClick={() => void start("document", d.id)}
              />
            ))}
            <StartOption
              icon={<FileText size={18} aria-hidden />}
              title={t("jd.mgr.fromBlank", "Start from the PME template")}
              hint={t("jd.mgr.fromBlankHint", "The standard layout with the five competencies filled in.")}
              disabled={starting}
              primary={!current && page.importable.length === 0}
              onClick={() => void start("blank")}
            />
          </div>
        </div>
      )}

      {draft && content && (
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3 sm:px-5">
            <div>
              <h2 className="text-base font-bold text-pme-navy">{t("jd.mgr.draftTitle", "Draft · version {{n}}", { n: draft.version })}</h2>
              <p className="text-xs text-gray-500">{t("jd.mgr.draftHint", "Only you and HR can see this until it's issued.")}</p>
            </div>
            <div className="flex rounded-xl bg-gray-100 p-1 text-sm font-semibold" role="tablist">
              <button type="button" role="tab" aria-selected={!preview} onClick={() => setPreview(false)} className={`min-h-[36px] rounded-lg px-3 ${!preview ? "bg-white text-pme-navy shadow-sm" : "text-gray-500"}`}>
                {t("jd.mgr.edit", "Edit")}
              </button>
              <button type="button" role="tab" aria-selected={preview} onClick={() => setPreview(true)} className={`inline-flex min-h-[36px] items-center gap-1 rounded-lg px-3 ${preview ? "bg-white text-pme-navy shadow-sm" : "text-gray-500"}`}>
                <Eye size={14} aria-hidden /> {t("jd.mgr.preview", "Preview")}
              </button>
            </div>
          </div>
          <div className="p-4 sm:p-5">
            {draft.source_document && !preview && (
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-xl bg-gray-50 px-3 py-2 text-sm text-gray-700">
                <span className="inline-flex min-w-0 items-center gap-2">
                  <FileText size={16} className="shrink-0 text-gray-400" aria-hidden />
                  <span className="min-w-0">{t("jd.mgr.readFrom", "Read from “{{title}}”", { title: draft.source_document.title })}</span>
                </span>
                <button type="button" disabled={busy} onClick={() => void reread()} className="inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-50">
                  <RefreshCw size={14} aria-hidden /> {t("jd.mgr.reread", "Read the file again")}
                </button>
              </div>
            )}
            {draft.source_document?.signed_by_employee && !preview && (
              <div className="mb-5 rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-900">
                <p className="flex items-center gap-2 font-semibold">
                  <CheckCircle2 size={16} aria-hidden />
                  {t("jd.mgr.alreadySignedTitle", "{{name}} already signed this file on {{date}}", { name: first, date: jdDate(draft.source_document.signed_at) })}
                </p>
                <p className="mt-1">
                  {t("jd.mgr.alreadySignedHint", "Check it reads the same as the file, then record it as signed. {{name}} doesn't need to sign again and nobody is emailed.", { name: first })}
                </p>
                {problems.length > 0 && (
                  <ul className="mt-2 list-disc space-y-0.5 pl-5 text-amber-900">
                    {problems.map((p) => <li key={p}>{p}</li>)}
                  </ul>
                )}
                <button
                  type="button"
                  disabled={busy || problems.length > 0}
                  onClick={() => void adoptSigned()}
                  className="mt-3 inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-green-700 px-4 text-sm font-semibold text-white disabled:opacity-50"
                >
                  <CheckCircle2 size={15} aria-hidden /> {t("jd.mgr.adopt", "Record as signed on {{date}}", { date: jdDate(draft.source_document.signed_at) })}
                </button>
              </div>
            )}
            {draft.import_notes.length > 0 && !preview && (
              <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <p className="flex items-center gap-2 font-semibold">
                  <AlertTriangle size={16} aria-hidden /> {t("jd.mgr.importTitle", "Read from the uploaded file. Please check it.")}
                </p>
                <ul className="mt-1 list-disc space-y-0.5 pl-5">
                  {draft.import_notes.map((n, i) => <li key={i}>{n}</li>)}
                </ul>
              </div>
            )}
            {preview ? <JobDescriptionView content={content} /> : <JobDescriptionEditor content={content} onChange={change} />}
          </div>
          <div className="sticky bottom-16 z-10 flex items-center gap-2 rounded-b-2xl border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur sm:bottom-0 sm:px-5">
            <span className={`min-w-0 flex-1 text-xs font-semibold ${saveState === "error" ? "text-red-700" : "text-gray-500"}`} role="status">
              {saveState === "saving" && t("jd.mgr.saving", "Saving…")}
              {saveState === "unsaved" && t("jd.mgr.unsaved", "Changes not saved yet")}
              {saveState === "saved" && t("jd.mgr.saved", "All changes saved")}
              {saveState === "error" && t("jd.mgr.saveError", "Couldn't save. We'll try again when you next change something.")}
            </span>
            <button type="button" onClick={() => void discard()} aria-label={t("jd.mgr.discard", "Discard draft")} title={t("jd.mgr.discard", "Discard draft")} className="inline-flex min-h-[44px] items-center gap-1.5 rounded-xl px-3 text-sm font-semibold text-gray-600 hover:bg-gray-100">
              <Trash2 size={15} aria-hidden /> <span className="hidden sm:inline">{t("jd.mgr.discard", "Discard draft")}</span>
            </button>
            <button
              type="button"
              onClick={async () => {
                window.clearTimeout(timer.current);
                if (await save()) setIssuing(true);
              }}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-pme-blue px-5 text-sm font-semibold text-white"
            >
              <Send size={15} aria-hidden /> <span className="whitespace-nowrap">{t("jd.mgr.issue", "Issue to {{name}}", { name: first })}</span>
            </button>
          </div>
        </div>
      )}

      <History page={page} />

      {issuing && draft && content && (
        <IssueDialog
          page={page}
          draft={draft}
          problems={problems}
          onClose={async (result) => {
            setIssuing(false);
            if (result) {
              setNotice({ tone: result.sent ? "ok" : "warn", text: result.text });
              await reload();
            }
          }}
        />
      )}
    </div>
  );
}

function StatusCard({
  current,
  hasDraft,
  first,
  onResend,
}: {
  current: JDVersion | null;
  hasDraft: boolean;
  first: string;
  onResend: (id: number) => void;
}) {
  const { t } = useTranslation();
  if (!current) {
    return (
      <Banner tone="grey" icon={<FileText size={18} aria-hidden />} title={t("jd.mgr.noneTitle", "No job description in PME People yet")}>
        {hasDraft ? t("jd.mgr.noneDraft", "You have a draft below. Issue it when it's ready.") : t("jd.mgr.noneHint", "Write one below, or read it in from a file already uploaded for {{name}}.", { name: first })}
      </Banner>
    );
  }
  const download = (
    <a href={pdfUrl(current.id, true)} className="inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 hover:bg-gray-50">
      <Download size={14} aria-hidden /> {t("jd.downloadPdf", "Download PDF")}
    </a>
  );
  if (current.status === "signed") {
    return (
      <Banner tone="green" icon={<CheckCircle2 size={18} aria-hidden />} title={t("jd.mgr.signedTitle", "Version {{n}} signed by {{name}} on {{date}}", { n: current.version, name: first, date: jdDate(current.signed_at) })} action={download}>
        {current.summary}
        {current.signed_on_upload && (
          <span className="mt-1 block text-xs">{t("jd.mgr.signedOnUpload", "Signed on the uploaded file, then recorded in PME People.")}</span>
        )}
      </Banner>
    );
  }
  if (current.status === "changes_requested") {
    return (
      <Banner tone="amber" icon={<MessageSquare size={18} aria-hidden />} title={t("jd.mgr.askedTitle", "{{name}} asked for a change to version {{n}}", { name: first, n: current.version })} action={download}>
        <span className="block italic">“{current.change_comment}”</span>
        <span className="mt-1 block">
          {hasDraft ? t("jd.mgr.askedDraft", "Make the change in the draft below, then issue it again.") : t("jd.mgr.askedHint", "Choose “Edit the current version”, make the change and issue it again.")}
        </span>
      </Banner>
    );
  }
  return (
    <Banner
      tone="blue"
      icon={<Clock size={18} aria-hidden />}
      title={t("jd.mgr.waitingTitle", "Version {{n}} sent to {{name}} on {{date}}, waiting for them to sign", { n: current.version, name: first, date: jdDate(current.issued_at) })}
      action={
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => onResend(current.id)} className="inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 hover:bg-gray-50">
            <Send size={14} aria-hidden /> {t("jd.mgr.resend", "Send the email again")}
          </button>
          {download}
        </div>
      }
    >
      {current.summary}
    </Banner>
  );
}

function IssueDialog({
  page,
  draft,
  problems,
  onClose,
}: {
  page: JDPage;
  draft: JDVersion;
  problems: string[];
  onClose: (result: { sent: boolean; text: string } | null) => void;
}) {
  const { t } = useTranslation();
  const first = page.employee.name.split(" ")[0];
  const withdrawable = page.importable.filter((d) => !d.signed);
  const [withdraw, setWithdraw] = useState<Set<number>>(() => new Set(withdrawable.filter((d) => d.looks_like_jd).map((d) => d.id)));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const issue = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.post<{ sent: boolean; message: string; withdrawn: string[] }>(`/api/job-descriptions/${draft.id}/issue`, {
        withdraw_document_ids: [...withdraw],
      });
      const removed = r.withdrawn.length ? ` ${t("jd.mgr.withdrawn", "Taken out of their documents: {{list}}.", { list: r.withdrawn.join(", ") })}` : "";
      onClose({ sent: r.sent, text: `${t("jd.mgr.issued", "Version {{n}} issued.", { n: draft.version })} ${r.message}${removed}` });
    } catch (e) {
      setErr(errText(e));
      setBusy(false);
    }
  };

  return (
    <Dialog title={t("jd.mgr.issueTitle", "Issue to {{name}}", { name: first })} onClose={() => onClose(null)}>
      {problems.length > 0 ? (
        <>
          <p className="text-sm font-semibold text-amber-900">{t("jd.mgr.notReady", "A few things to finish first:")}</p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-700">
            {problems.map((p, i) => <li key={i}>{p}</li>)}
          </ul>
          <div className="mt-4 flex justify-end">
            <button type="button" onClick={() => onClose(null)} className="min-h-[44px] rounded-xl bg-pme-blue px-5 text-sm font-semibold text-white">
              {t("jd.mgr.backToEdit", "Back to the draft")}
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-gray-700">
            {t("jd.mgr.issueHint", "{{name}} gets an email with a link to read it and sign, or ask for a change. Today's date goes in as the date last reviewed.", { name: first })}
            {page.current ? ` ${t("jd.mgr.issueReplaces", "It replaces version {{n}}.", { n: page.current.version })}` : ""}
          </p>
          {withdrawable.length > 0 && (
            <fieldset className="mt-4 rounded-xl border border-gray-200 p-3">
              <legend className="px-1 text-xs font-semibold text-gray-600">{t("jd.mgr.withdrawTitle", "Take old files out of {{name}}'s documents?", { name: first })}</legend>
              <p className="mb-2 text-xs text-gray-500">{t("jd.mgr.withdrawHint", "So they don't sign an old version by mistake. Nothing is deleted; HR can still see them.")}</p>
              {withdrawable.map((d) => (
                <label key={d.id} className="flex min-h-[40px] items-center gap-2 text-sm text-gray-800">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-pme-blue"
                    checked={withdraw.has(d.id)}
                    onChange={(e) => {
                      const next = new Set(withdraw);
                      if (e.target.checked) next.add(d.id);
                      else next.delete(d.id);
                      setWithdraw(next);
                    }}
                  />
                  <span className="min-w-0 break-words">{d.title}</span>
                  {d.requires_signature && <span className="shrink-0 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">{t("jd.mgr.awaitingSig", "waiting for signature")}</span>}
                </label>
              ))}
            </fieldset>
          )}
          {err && <p className="mt-3 text-sm text-red-700">{err}</p>}
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={() => onClose(null)} className="min-h-[44px] rounded-xl px-4 text-sm font-semibold text-gray-600">
              {t("common.cancel", "Cancel")}
            </button>
            <button type="button" disabled={busy} onClick={() => void issue()} className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-pme-blue px-5 text-sm font-semibold text-white disabled:opacity-40">
              <Send size={15} aria-hidden /> {t("jd.mgr.issueSend", "Issue and email {{name}}", { name: first })}
            </button>
          </div>
        </>
      )}
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* Shared bits                                                          */
/* ------------------------------------------------------------------ */

const STATUS_LABEL: Record<string, [string, string, string]> = {
  issued: ["jd.status.issued", "Waiting for signature", "bg-blue-50 text-pme-blue"],
  changes_requested: ["jd.status.changes_requested", "Change asked for", "bg-amber-50 text-amber-800"],
  signed: ["jd.status.signed", "Signed", "bg-green-50 text-green-700"],
  superseded: ["jd.status.superseded", "Replaced", "bg-gray-100 text-gray-600"],
};

function History({ page }: { page: JDPage }) {
  const { t } = useTranslation();
  if (page.history.length === 0) return null;
  return (
    <section>
      <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-500">{t("jd.history", "Versions")}</h2>
      <ul className="divide-y divide-gray-100 rounded-2xl border border-gray-200 bg-white">
        {page.history.map((v) => {
          const [key, label, cls] = STATUS_LABEL[v.status] ?? ["", v.status, "bg-gray-100 text-gray-600"];
          return (
            <li key={v.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3">
              <span className="text-sm font-semibold text-gray-900">{t("jd.version", "Version {{n}}", { n: v.version })}</span>
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${cls}`}>{key ? t(key, label) : label}</span>
              <span className="text-xs text-gray-500">
                {v.signed_at
                  ? t("jd.signedOn", "signed {{date}}", { date: jdDate(v.signed_at) })
                  : v.issued_at
                    ? t("jd.issuedOn", "issued {{date}}", { date: jdDate(v.issued_at) })
                    : ""}
              </span>
              <span className="flex-1" />
              <a href={pdfUrl(v.id)} target="_blank" rel="noreferrer" className="inline-flex min-h-[36px] items-center gap-1 text-xs font-semibold text-pme-blue">
                <FileText size={14} aria-hidden /> PDF
              </a>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function StartOption({
  icon,
  title,
  hint,
  onClick,
  disabled,
  primary,
}: {
  icon: React.ReactNode;
  title: string;
  hint: string;
  onClick: () => void;
  disabled?: boolean;
  primary?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`flex min-h-[64px] items-start gap-3 rounded-xl border p-3 text-left transition disabled:opacity-50 ${primary ? "border-pme-blue bg-pme-sky/40 hover:bg-pme-sky/60" : "border-gray-200 hover:bg-gray-50"}`}
    >
      <span className={`mt-0.5 shrink-0 ${primary ? "text-pme-blue" : "text-gray-500"}`}>{icon}</span>
      <span className="min-w-0">
        <span className="block break-words text-sm font-semibold text-gray-900">{title}</span>
        <span className="mt-0.5 block text-xs text-gray-600">{hint}</span>
      </span>
    </button>
  );
}

function Banner({
  tone,
  icon,
  title,
  children,
  action,
}: {
  tone: "blue" | "amber" | "green" | "grey";
  icon: React.ReactNode;
  title: string;
  children?: React.ReactNode;
  action?: React.ReactNode;
}) {
  const tones = {
    blue: "border-pme-blue/30 bg-pme-sky/40 text-pme-navy",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    green: "border-green-200 bg-green-50 text-green-900",
    grey: "border-gray-200 bg-gray-50 text-gray-800",
  };
  return (
    <div className={`rounded-2xl border p-4 ${tones[tone]}`}>
      <div className="flex items-start gap-2">
        <span className="mt-0.5 shrink-0">{icon}</span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold">{title}</p>
          {children && <div className="mt-1 text-sm opacity-90">{children}</div>}
          {action && <div className="mt-3">{action}</div>}
        </div>
      </div>
    </div>
  );
}

function Dialog({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="max-h-[90dvh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-5 shadow-xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-2 text-lg font-bold text-pme-navy">{title}</h2>
        {children}
      </div>
    </div>
  );
}
